/** Shared exterior/interior frame colors across all configurators (localStorage). */
export const FRAME_COLORS_STORAGE_KEY = 'config-go-frame-colors';

export const KOZIJNEN_COLOR_FIELDS = {
  exteriorField: 'color',
  interiorField: 'interiorFrameColor',
};

export const DOOR_COLOR_FIELDS = {
  exteriorField: 'exteriorColor',
  interiorField: 'interiorColor',
};

export function loadPersistedFrameColors() {
  try {
    const raw = localStorage.getItem(FRAME_COLORS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.exteriorColor && parsed?.interiorColor) {
      return {
        exteriorColor: String(parsed.exteriorColor),
        interiorColor: String(parsed.interiorColor),
      };
    }
  } catch {
    // ignore corrupt storage
  }
  return null;
}

export function savePersistedFrameColors(exteriorColor, interiorColor) {
  if (!exteriorColor || !interiorColor) return;
  localStorage.setItem(
    FRAME_COLORS_STORAGE_KEY,
    JSON.stringify({
      exteriorColor: String(exteriorColor),
      interiorColor: String(interiorColor),
    })
  );
}

/**
 * Merge saved frame colors into a page default config.
 */
export function applyPersistedFrameColors(
  defaultConfig,
  { exteriorField = 'exteriorColor', interiorField = 'interiorColor' } = {}
) {
  const saved = loadPersistedFrameColors();
  if (!saved) return { ...defaultConfig };

  return {
    ...defaultConfig,
    [exteriorField]: saved.exteriorColor,
    [interiorField]: saved.interiorColor,
  };
}

/**
 * Persist when user changes exterior or interior on any configurator page.
 */
export function trackFrameColorChange(
  field,
  value,
  { exteriorField = 'exteriorColor', interiorField = 'interiorColor' } = {}
) {
  if (!value) return;

  const saved = loadPersistedFrameColors() || {
    exteriorColor: 'anthracite',
    interiorColor: 'ral-9016',
  };

  if (field === exteriorField) {
    savePersistedFrameColors(value, saved.interiorColor);
  } else if (field === interiorField) {
    savePersistedFrameColors(saved.exteriorColor, value);
  }
}

export function createInitialConfiguratorConfig(defaultConfig, colorFields = DOOR_COLOR_FIELDS) {
  return () => applyPersistedFrameColors(defaultConfig, colorFields);
}

/** Thin stroke for frame edges — visible on white paper when frame fill is light. */
export const FRAME_EDGE_STROKE_DARK = '#1F2937';
export const FRAME_EDGE_STROKE_DEFAULT = '#D0D4DA';

export function isLightFrameColor(hex) {
  const normalized = String(hex || '').replace('#', '').trim();
  if (normalized.length !== 6) return false;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  if ([r, g, b].some((value) => Number.isNaN(value))) return false;
  return (r * 299 + g * 587 + b * 114) / 1000 >= 220;
}

export function shouldUsePrintableFrameEdges(exteriorHex, interiorHex) {
  return isLightFrameColor(exteriorHex) || isLightFrameColor(interiorHex);
}

export function getFrameEdgeStroke(hex) {
  return isLightFrameColor(hex) ? FRAME_EDGE_STROKE_DARK : FRAME_EDGE_STROKE_DEFAULT;
}
